{
  "version": "0.1.38",
  "packageName": "@classic-homes/native",
  "description": "Native device APIs for iOS and Android mobile apps. Uses Capacitor for native functionality with web fallbacks.",
  "subpackages": {
    "core": {
      "importPath": "@classic-homes/native/core",
      "description": "Framework-agnostic native APIs core with platform detection, adapters, and error handling",
      "functions": [
        {
          "name": "initNative",
          "signature": "initNative(config?: NativeConfig): PlatformInfo",
          "description": "Initialize the native module with optional configuration. Call at app startup.",
          "example": "const platform = initNative({ debug: true });"
        },
        {
          "name": "getPlatformInfo",
          "signature": "getPlatformInfo(): PlatformInfo",
          "description": "Get current platform information including OS, shell type, and capabilities."
        },
        {
          "name": "isNative",
          "signature": "isNative(): boolean",
          "description": "Check if running in a native Capacitor container (iOS/Android)."
        },
        {
          "name": "isWeb",
          "signature": "isWeb(): boolean",
          "description": "Check if running in a web browser without native container."
        },
        {
          "name": "isIOS",
          "signature": "isIOS(): boolean",
          "description": "Check if running on iOS."
        },
        {
          "name": "isAndroid",
          "signature": "isAndroid(): boolean",
          "description": "Check if running on Android."
        },
        {
          "name": "isMobile",
          "signature": "isMobile(): boolean",
          "description": "Check if running on a mobile device (iOS or Android)."
        },
        {
          "name": "isSSR",
          "signature": "isSSR(): boolean",
          "description": "Check if running in server-side rendering context."
        },
        {
          "name": "detectAvailableFeatures",
          "signature": "detectAvailableFeatures(): Promise<{ available: NativeFeature[]; unavailable: NativeFeature[] }>",
          "description": "Detect which native features are available on the current platform."
        }
      ],
      "types": [
        {
          "name": "NativeConfig",
          "description": "Configuration options for native initialization",
          "properties": [
            {
              "name": "debug",
              "type": "boolean",
              "required": false,
              "description": "Enable debug logging"
            },
            {
              "name": "geolocationDefaults",
              "type": "GeolocationOptions",
              "required": false,
              "description": "Default geolocation options"
            },
            {
              "name": "cameraDefaults",
              "type": "CameraOptions",
              "required": false,
              "description": "Default camera options"
            }
          ]
        },
        {
          "name": "PlatformInfo",
          "description": "Information about the current platform",
          "properties": [
            {
              "name": "platform",
              "type": "'ios' | 'android' | 'web'",
              "required": true,
              "description": "Specific platform"
            },
            {
              "name": "shellType",
              "type": "'capacitor' | 'web'",
              "required": true,
              "description": "Native shell type"
            },
            {
              "name": "category",
              "type": "'mobile' | 'web'",
              "required": true,
              "description": "Platform category"
            },
            {
              "name": "isNative",
              "type": "boolean",
              "required": true,
              "description": "Whether running in native container"
            },
            {
              "name": "isMobile",
              "type": "boolean",
              "required": true,
              "description": "Whether running on mobile"
            }
          ]
        },
        {
          "name": "NativeFeature",
          "description": "Available native feature identifiers",
          "properties": [
            {
              "name": "type",
              "type": "'camera' | 'geolocation' | 'secureStorage' | 'biometrics' | 'haptics' | 'device' | 'network' | 'pushNotifications' | 'appLifecycle'",
              "required": true,
              "description": "Feature identifier"
            }
          ]
        }
      ]
    },
    "react": {
      "importPath": "@classic-homes/native/react",
      "description": "React hooks and components for native APIs",
      "hooks": [
        {
          "name": "useGeolocation",
          "signature": "useGeolocation(options?: UseGeolocationOptions): UseGeolocationReturn",
          "description": "Hook for GPS location with reactive state. Supports one-time fetch and continuous watching.",
          "options": [
            {
              "name": "enableHighAccuracy",
              "type": "boolean",
              "required": false,
              "description": "Enable GPS for higher accuracy"
            },
            {
              "name": "timeout",
              "type": "number",
              "required": false,
              "description": "Timeout in milliseconds"
            },
            {
              "name": "maximumAge",
              "type": "number",
              "required": false,
              "description": "Maximum age of cached position"
            },
            {
              "name": "autoFetch",
              "type": "boolean",
              "required": false,
              "description": "Automatically get position on mount"
            },
            {
              "name": "autoWatch",
              "type": "boolean",
              "required": false,
              "description": "Automatically start watching on mount"
            }
          ],
          "returns": [
            {
              "name": "position",
              "type": "Position | null",
              "required": true,
              "description": "Current position"
            },
            {
              "name": "isLoading",
              "type": "boolean",
              "required": true,
              "description": "Whether currently fetching"
            },
            {
              "name": "isWatching",
              "type": "boolean",
              "required": true,
              "description": "Whether watching position"
            },
            {
              "name": "error",
              "type": "Error | null",
              "required": true,
              "description": "Current error"
            },
            {
              "name": "getCurrentPosition",
              "type": "() => Promise<Position>",
              "required": true,
              "description": "Get position once"
            },
            {
              "name": "startWatching",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Start watching"
            },
            {
              "name": "stopWatching",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Stop watching"
            },
            {
              "name": "requestPermissions",
              "type": "() => Promise<PermissionState>",
              "required": true,
              "description": "Request permissions"
            }
          ],
          "example": "const { position, getCurrentPosition, isLoading } = useGeolocation({ enableHighAccuracy: true });"
        },
        {
          "name": "useCamera",
          "signature": "useCamera(options?: UseCameraOptions): UseCameraReturn",
          "description": "Hook for camera capture and photo selection from gallery.",
          "options": [
            {
              "name": "quality",
              "type": "number",
              "required": false,
              "description": "Photo quality (0-100)"
            },
            {
              "name": "allowEditing",
              "type": "boolean",
              "required": false,
              "description": "Allow editing after capture"
            },
            {
              "name": "resultType",
              "type": "'base64' | 'uri' | 'dataUrl'",
              "required": false,
              "description": "Result format"
            },
            {
              "name": "source",
              "type": "'camera' | 'photos' | 'prompt'",
              "required": false,
              "description": "Image source"
            }
          ],
          "returns": [
            {
              "name": "photo",
              "type": "CameraPhoto | null",
              "required": true,
              "description": "Captured photo"
            },
            {
              "name": "isLoading",
              "type": "boolean",
              "required": true,
              "description": "Whether capturing"
            },
            {
              "name": "error",
              "type": "Error | null",
              "required": true,
              "description": "Current error"
            },
            {
              "name": "takePhoto",
              "type": "() => Promise<CameraPhoto>",
              "required": true,
              "description": "Capture from camera"
            },
            {
              "name": "pickFromGallery",
              "type": "() => Promise<CameraPhoto>",
              "required": true,
              "description": "Select from gallery"
            },
            {
              "name": "requestPermissions",
              "type": "() => Promise<PermissionState>",
              "required": true,
              "description": "Request permissions"
            }
          ],
          "example": "const { photo, takePhoto, pickFromGallery } = useCamera({ quality: 90 });"
        },
        {
          "name": "useBiometrics",
          "signature": "useBiometrics(options?: UseBiometricsOptions): UseBiometricsReturn",
          "description": "Hook for biometric authentication (Face ID, Touch ID, fingerprint).",
          "returns": [
            {
              "name": "isAvailable",
              "type": "boolean",
              "required": true,
              "description": "Whether biometrics available"
            },
            {
              "name": "availability",
              "type": "BiometricAvailability | null",
              "required": true,
              "description": "Detailed availability info"
            },
            {
              "name": "isLoading",
              "type": "boolean",
              "required": true,
              "description": "Whether authenticating"
            },
            {
              "name": "error",
              "type": "Error | null",
              "required": true,
              "description": "Current error"
            },
            {
              "name": "authenticate",
              "type": "(options?: BiometricOptions) => Promise<BiometricResult>",
              "required": true,
              "description": "Perform authentication"
            },
            {
              "name": "checkAvailability",
              "type": "() => Promise<BiometricAvailability>",
              "required": true,
              "description": "Check availability"
            }
          ],
          "example": "const { isAvailable, authenticate } = useBiometrics();"
        },
        {
          "name": "useSecureStorage",
          "signature": "useSecureStorage(): UseSecureStorageReturn",
          "description": "Hook for secure key-value storage (Keychain on iOS, EncryptedPrefs on Android).",
          "returns": [
            {
              "name": "isLoading",
              "type": "boolean",
              "required": true,
              "description": "Whether operation in progress"
            },
            {
              "name": "error",
              "type": "Error | null",
              "required": true,
              "description": "Current error"
            },
            {
              "name": "get",
              "type": "(key: string) => Promise<string | null>",
              "required": true,
              "description": "Get value by key"
            },
            {
              "name": "set",
              "type": "(key: string, value: string) => Promise<void>",
              "required": true,
              "description": "Set value"
            },
            {
              "name": "remove",
              "type": "(key: string) => Promise<void>",
              "required": true,
              "description": "Remove value"
            },
            {
              "name": "clear",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Clear all values"
            },
            {
              "name": "keys",
              "type": "() => Promise<string[]>",
              "required": true,
              "description": "Get all keys"
            }
          ],
          "example": "const { get, set, remove } = useSecureStorage();"
        },
        {
          "name": "useHaptics",
          "signature": "useHaptics(): UseHapticsReturn",
          "description": "Hook for haptic feedback (vibration, impact, notification).",
          "returns": [
            {
              "name": "isAvailable",
              "type": "boolean",
              "required": true,
              "description": "Whether haptics available"
            },
            {
              "name": "impact",
              "type": "(style: 'light' | 'medium' | 'heavy') => Promise<void>",
              "required": true,
              "description": "Trigger impact feedback"
            },
            {
              "name": "notification",
              "type": "(type: 'success' | 'warning' | 'error') => Promise<void>",
              "required": true,
              "description": "Trigger notification feedback"
            },
            {
              "name": "vibrate",
              "type": "(duration?: number) => Promise<void>",
              "required": true,
              "description": "Vibrate for duration"
            }
          ],
          "example": "const { impact, notification } = useHaptics();"
        },
        {
          "name": "useDevice",
          "signature": "useDevice(options?: UseDeviceOptions): UseDeviceReturn",
          "description": "Hook for device information (model, OS version, battery).",
          "returns": [
            {
              "name": "info",
              "type": "DeviceInfo | null",
              "required": true,
              "description": "Device information"
            },
            {
              "name": "battery",
              "type": "BatteryInfo | null",
              "required": true,
              "description": "Battery information"
            },
            {
              "name": "language",
              "type": "string | null",
              "required": true,
              "description": "Device language"
            },
            {
              "name": "isLoading",
              "type": "boolean",
              "required": true,
              "description": "Whether loading"
            },
            {
              "name": "refresh",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Refresh device info"
            }
          ],
          "example": "const { info, battery } = useDevice({ autoFetch: true });"
        },
        {
          "name": "useNetwork",
          "signature": "useNetwork(options?: UseNetworkOptions): UseNetworkReturn",
          "description": "Hook for network connectivity status.",
          "returns": [
            {
              "name": "status",
              "type": "NetworkStatus | null",
              "required": true,
              "description": "Full network status"
            },
            {
              "name": "isConnected",
              "type": "boolean",
              "required": true,
              "description": "Whether connected"
            },
            {
              "name": "connectionType",
              "type": "'wifi' | 'cellular' | 'none' | 'unknown'",
              "required": true,
              "description": "Connection type"
            },
            {
              "name": "refresh",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Refresh status"
            }
          ],
          "example": "const { isConnected, connectionType } = useNetwork();"
        },
        {
          "name": "useAppLifecycle",
          "signature": "useAppLifecycle(options?: UseAppLifecycleOptions): UseAppLifecycleReturn",
          "description": "Hook for app lifecycle events (foreground/background, deep links).",
          "options": [
            {
              "name": "onStateChange",
              "type": "(state: AppStateChange) => void",
              "required": false,
              "description": "Called when app state changes"
            },
            {
              "name": "onUrlOpen",
              "type": "(event: AppUrlOpen) => void",
              "required": false,
              "description": "Called when app opened via URL"
            }
          ],
          "returns": [
            {
              "name": "isActive",
              "type": "boolean",
              "required": true,
              "description": "Whether app is active"
            },
            {
              "name": "launchUrl",
              "type": "string | null",
              "required": true,
              "description": "URL that launched app"
            },
            {
              "name": "exitApp",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Exit app (Android)"
            },
            {
              "name": "minimizeApp",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Minimize app (Android)"
            }
          ],
          "example": "const { isActive } = useAppLifecycle({ onStateChange: (s) => console.log(s) });"
        },
        {
          "name": "usePushNotifications",
          "signature": "usePushNotifications(options?: UsePushNotificationsOptions): UsePushNotificationsReturn",
          "description": "Hook for push notifications (APNs on iOS, FCM on Android).",
          "options": [
            {
              "name": "onNotificationReceived",
              "type": "(notification: PushNotification) => void",
              "required": false,
              "description": "Called when notification received"
            },
            {
              "name": "onNotificationAction",
              "type": "(action: PushNotificationAction) => void",
              "required": false,
              "description": "Called when user taps notification"
            }
          ],
          "returns": [
            {
              "name": "token",
              "type": "PushToken | null",
              "required": true,
              "description": "Registration token"
            },
            {
              "name": "isRegistered",
              "type": "boolean",
              "required": true,
              "description": "Whether registered"
            },
            {
              "name": "permissionState",
              "type": "PermissionState | null",
              "required": true,
              "description": "Permission state"
            },
            {
              "name": "register",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Register for push"
            },
            {
              "name": "unregister",
              "type": "() => Promise<void>",
              "required": true,
              "description": "Unregister"
            },
            {
              "name": "requestPermissions",
              "type": "() => Promise<PermissionState>",
              "required": true,
              "description": "Request permissions"
            },
            {
              "name": "createChannel",
              "type": "(channel: NotificationChannel) => Promise<void>",
              "required": true,
              "description": "Create Android channel"
            }
          ],
          "example": "const { token, register } = usePushNotifications({ onNotificationReceived: (n) => console.log(n) });"
        },
        {
          "name": "usePlatformCheck",
          "signature": "usePlatformCheck(): UsePlatformCheckReturn",
          "description": "Hook for platform detection and feature availability checks.",
          "returns": [
            {
              "name": "isNative",
              "type": "boolean",
              "required": true,
              "description": "Running in native container"
            },
            {
              "name": "isWeb",
              "type": "boolean",
              "required": true,
              "description": "Running in web browser"
            },
            {
              "name": "isIOS",
              "type": "boolean",
              "required": true,
              "description": "Running on iOS"
            },
            {
              "name": "isAndroid",
              "type": "boolean",
              "required": true,
              "description": "Running on Android"
            },
            {
              "name": "platform",
              "type": "'ios' | 'android' | 'web'",
              "required": true,
              "description": "Current platform"
            },
            {
              "name": "isFeatureAvailable",
              "type": "(feature: NativeFeature) => Promise<boolean>",
              "required": true,
              "description": "Check feature availability"
            }
          ],
          "example": "const { isNative, isFeatureAvailable } = usePlatformCheck();"
        }
      ]
    },
    "svelte": {
      "importPath": "@classic-homes/native/svelte",
      "description": "Svelte 5 composables using runes ($state, $effect) for native APIs",
      "composables": [
        {
          "name": "useGeolocation",
          "signature": "useGeolocation(options?: UseGeolocationOptions): UseGeolocationReturn",
          "description": "Composable for GPS location with reactive state using Svelte 5 runes.",
          "example": "const geo = useGeolocation({ enableHighAccuracy: true });\n// Access: geo.position, geo.isLoading, geo.getCurrentPosition()"
        },
        {
          "name": "useCamera",
          "signature": "useCamera(options?: UseCameraOptions): UseCameraReturn",
          "description": "Composable for camera capture and photo selection.",
          "example": "const camera = useCamera({ quality: 90 });\n// Access: camera.photo, camera.takePhoto(), camera.pickFromGallery()"
        },
        {
          "name": "useBiometrics",
          "signature": "useBiometrics(options?: UseBiometricsOptions): UseBiometricsReturn",
          "description": "Composable for biometric authentication.",
          "example": "const bio = useBiometrics();\n// Access: bio.isAvailable, bio.authenticate()"
        },
        {
          "name": "useSecureStorage",
          "signature": "useSecureStorage(): UseSecureStorageReturn",
          "description": "Composable for secure key-value storage.",
          "example": "const storage = useSecureStorage();\n// Access: storage.get('key'), storage.set('key', 'value')"
        },
        {
          "name": "useHaptics",
          "signature": "useHaptics(): UseHapticsReturn",
          "description": "Composable for haptic feedback.",
          "example": "const haptics = useHaptics();\n// Access: haptics.impact('medium'), haptics.notification('success')"
        },
        {
          "name": "useDevice",
          "signature": "useDevice(options?: UseDeviceOptions): UseDeviceReturn",
          "description": "Composable for device information.",
          "example": "const device = useDevice();\n// Access: device.info, device.battery"
        },
        {
          "name": "useNetwork",
          "signature": "useNetwork(options?: UseNetworkOptions): UseNetworkReturn",
          "description": "Composable for network connectivity status.",
          "example": "const network = useNetwork();\n// Access: network.isConnected, network.connectionType"
        },
        {
          "name": "useAppLifecycle",
          "signature": "useAppLifecycle(options?: UseAppLifecycleOptions): UseAppLifecycleReturn",
          "description": "Composable for app lifecycle events.",
          "example": "const lifecycle = useAppLifecycle({ onStateChange: (s) => console.log(s) });\n// Access: lifecycle.isActive"
        },
        {
          "name": "usePushNotifications",
          "signature": "usePushNotifications(options?: UsePushNotificationsOptions): UsePushNotificationsReturn",
          "description": "Composable for push notifications.",
          "example": "const push = usePushNotifications();\n// Access: push.token, push.register()"
        },
        {
          "name": "usePlatformCheck",
          "signature": "usePlatformCheck(): UsePlatformCheckReturn",
          "description": "Composable for platform detection and feature availability checks.",
          "example": "const platform = usePlatformCheck();\n// Access: platform.isNative, platform.isFeatureAvailable('camera')"
        }
      ],
      "functions": [
        {
          "name": "checkPlatform",
          "signature": "checkPlatform(): PlatformCheckResult",
          "description": "Non-reactive platform check function."
        },
        {
          "name": "isNativeGuard",
          "signature": "isNativeGuard(): boolean",
          "description": "Check if running on native platform."
        },
        {
          "name": "isFeatureAvailableGuard",
          "signature": "isFeatureAvailableGuard(feature: NativeFeature): Promise<boolean>",
          "description": "Check if a feature is available (async)."
        },
        {
          "name": "checkFeaturesAvailability",
          "signature": "checkFeaturesAvailability(): Promise<{ available: NativeFeature[]; unavailable: NativeFeature[]; isFeatureAvailable: (f: NativeFeature) => boolean }>",
          "description": "Check multiple features availability."
        }
      ]
    }
  },
  "features": [
    {
      "name": "Camera",
      "hook": "useCamera",
      "description": "Photo capture and gallery selection",
      "ios": "Native camera API",
      "android": "Native camera API",
      "webFallback": "File input element",
      "capacitorPlugin": "@capacitor/camera"
    },
    {
      "name": "Geolocation",
      "hook": "useGeolocation",
      "description": "GPS location tracking",
      "ios": "Core Location",
      "android": "Location Services",
      "webFallback": "Navigator Geolocation API",
      "capacitorPlugin": "@capacitor/geolocation"
    },
    {
      "name": "Secure Storage",
      "hook": "useSecureStorage",
      "description": "Encrypted key-value storage",
      "ios": "Keychain",
      "android": "EncryptedSharedPreferences",
      "webFallback": "localStorage",
      "capacitorPlugin": "@capacitor/preferences"
    },
    {
      "name": "Biometrics",
      "hook": "useBiometrics",
      "description": "Biometric authentication",
      "ios": "Face ID / Touch ID",
      "android": "Fingerprint / Face",
      "webFallback": "WebAuthn",
      "capacitorPlugin": "@capgo/capacitor-native-biometric"
    },
    {
      "name": "Haptics",
      "hook": "useHaptics",
      "description": "Vibration and haptic feedback",
      "ios": "Taptic Engine",
      "android": "Vibrator",
      "webFallback": "Vibration API",
      "capacitorPlugin": "@capacitor/haptics"
    },
    {
      "name": "Device Info",
      "hook": "useDevice",
      "description": "Device model, OS, battery info",
      "ios": "UIDevice",
      "android": "Build + BatteryManager",
      "webFallback": "User Agent parsing",
      "capacitorPlugin": "@capacitor/device"
    },
    {
      "name": "Network Status",
      "hook": "useNetwork",
      "description": "Connectivity monitoring",
      "ios": "NWPathMonitor",
      "android": "ConnectivityManager",
      "webFallback": "Navigator.onLine",
      "capacitorPlugin": "@capacitor/network"
    },
    {
      "name": "Push Notifications",
      "hook": "usePushNotifications",
      "description": "Remote push notifications",
      "ios": "APNs",
      "android": "FCM",
      "webFallback": "Web Push API",
      "capacitorPlugin": "@capacitor/push-notifications"
    },
    {
      "name": "App Lifecycle",
      "hook": "useAppLifecycle",
      "description": "Foreground/background, deep links",
      "ios": "UIApplicationDelegate",
      "android": "Activity lifecycle",
      "webFallback": "Visibility API",
      "capacitorPlugin": "@capacitor/app"
    }
  ],
  "types": {
    "platform": {
      "description": "Platform detection and identification types",
      "types": [
        {
          "name": "Platform",
          "description": "Operating system platform",
          "properties": [
            {
              "name": "type",
              "type": "'ios' | 'android' | 'web'",
              "required": true,
              "description": "Platform identifier"
            }
          ]
        },
        {
          "name": "ShellType",
          "description": "Native container type",
          "properties": [
            {
              "name": "type",
              "type": "'capacitor' | 'web'",
              "required": true,
              "description": "Shell identifier"
            }
          ]
        },
        {
          "name": "PlatformInfo",
          "description": "Complete platform information",
          "properties": [
            {
              "name": "platform",
              "type": "Platform",
              "required": true,
              "description": "Current platform"
            },
            {
              "name": "shellType",
              "type": "ShellType",
              "required": true,
              "description": "Native shell type"
            },
            {
              "name": "category",
              "type": "'mobile' | 'web'",
              "required": true,
              "description": "Platform category"
            },
            {
              "name": "isNative",
              "type": "boolean",
              "required": true,
              "description": "In native container"
            },
            {
              "name": "isMobile",
              "type": "boolean",
              "required": true,
              "description": "On mobile device"
            }
          ]
        },
        {
          "name": "PermissionState",
          "description": "Permission status for a feature",
          "properties": [
            {
              "name": "status",
              "type": "'granted' | 'denied' | 'prompt' | 'unavailable'",
              "required": true,
              "description": "Permission status"
            },
            {
              "name": "canAskAgain",
              "type": "boolean",
              "required": false,
              "description": "Whether can re-prompt"
            }
          ]
        }
      ]
    },
    "camera": {
      "description": "Camera and photo capture types",
      "types": [
        {
          "name": "CameraOptions",
          "description": "Camera capture configuration",
          "properties": [
            {
              "name": "quality",
              "type": "number",
              "required": false,
              "description": "Photo quality (0-100)"
            },
            {
              "name": "allowEditing",
              "type": "boolean",
              "required": false,
              "description": "Allow editing after capture"
            },
            {
              "name": "resultType",
              "type": "'base64' | 'uri' | 'dataUrl'",
              "required": false,
              "description": "Result format"
            },
            {
              "name": "source",
              "type": "'camera' | 'photos' | 'prompt'",
              "required": false,
              "description": "Image source"
            },
            {
              "name": "width",
              "type": "number",
              "required": false,
              "description": "Target width"
            },
            {
              "name": "height",
              "type": "number",
              "required": false,
              "description": "Target height"
            },
            {
              "name": "direction",
              "type": "'front' | 'rear'",
              "required": false,
              "description": "Camera direction"
            },
            {
              "name": "saveToGallery",
              "type": "boolean",
              "required": false,
              "description": "Save to gallery (iOS)"
            }
          ]
        },
        {
          "name": "CameraPhoto",
          "description": "Captured photo result",
          "properties": [
            {
              "name": "base64String",
              "type": "string",
              "required": false,
              "description": "Base64 data (if resultType='base64')"
            },
            {
              "name": "dataUrl",
              "type": "string",
              "required": false,
              "description": "Data URL (if resultType='dataUrl')"
            },
            {
              "name": "path",
              "type": "string",
              "required": false,
              "description": "File path (if resultType='uri')"
            },
            {
              "name": "webPath",
              "type": "string",
              "required": false,
              "description": "Web-safe path for img src"
            },
            {
              "name": "format",
              "type": "string",
              "required": true,
              "description": "MIME type"
            },
            {
              "name": "exif",
              "type": "Record<string, unknown>",
              "required": false,
              "description": "EXIF data"
            }
          ]
        }
      ]
    },
    "geolocation": {
      "description": "Location and GPS types",
      "types": [
        {
          "name": "GeolocationOptions",
          "description": "Geolocation request configuration",
          "properties": [
            {
              "name": "enableHighAccuracy",
              "type": "boolean",
              "required": false,
              "description": "Use GPS for higher accuracy"
            },
            {
              "name": "timeout",
              "type": "number",
              "required": false,
              "description": "Timeout in milliseconds"
            },
            {
              "name": "maximumAge",
              "type": "number",
              "required": false,
              "description": "Max age of cached position"
            }
          ]
        },
        {
          "name": "Position",
          "description": "Geographic position result",
          "properties": [
            {
              "name": "coords",
              "type": "Coordinates",
              "required": true,
              "description": "Position coordinates"
            },
            {
              "name": "timestamp",
              "type": "number",
              "required": true,
              "description": "When position was acquired"
            }
          ]
        },
        {
          "name": "Coordinates",
          "description": "Geographic coordinates",
          "properties": [
            {
              "name": "latitude",
              "type": "number",
              "required": true,
              "description": "Latitude in degrees"
            },
            {
              "name": "longitude",
              "type": "number",
              "required": true,
              "description": "Longitude in degrees"
            },
            {
              "name": "accuracy",
              "type": "number",
              "required": true,
              "description": "Accuracy in meters"
            },
            {
              "name": "altitude",
              "type": "number | null",
              "required": true,
              "description": "Altitude in meters"
            },
            {
              "name": "altitudeAccuracy",
              "type": "number | null",
              "required": true,
              "description": "Altitude accuracy"
            },
            {
              "name": "heading",
              "type": "number | null",
              "required": true,
              "description": "Heading in degrees"
            },
            {
              "name": "speed",
              "type": "number | null",
              "required": true,
              "description": "Speed in m/s"
            }
          ]
        }
      ]
    },
    "storage": {
      "description": "Secure storage types",
      "types": [
        {
          "name": "StorageValue",
          "description": "Stored value with metadata",
          "properties": [
            {
              "name": "value",
              "type": "T",
              "required": true,
              "description": "The stored value"
            },
            {
              "name": "storedAt",
              "type": "number",
              "required": false,
              "description": "Timestamp when stored"
            }
          ]
        }
      ]
    },
    "biometrics": {
      "description": "Biometric authentication types",
      "types": [
        {
          "name": "BiometricOptions",
          "description": "Authentication prompt configuration",
          "properties": [
            {
              "name": "reason",
              "type": "string",
              "required": false,
              "description": "Reason shown to user"
            },
            {
              "name": "title",
              "type": "string",
              "required": false,
              "description": "Prompt title (Android)"
            },
            {
              "name": "subtitle",
              "type": "string",
              "required": false,
              "description": "Prompt subtitle (Android)"
            },
            {
              "name": "negativeButtonText",
              "type": "string",
              "required": false,
              "description": "Cancel button text (Android)"
            },
            {
              "name": "allowDeviceCredential",
              "type": "boolean",
              "required": false,
              "description": "Allow PIN/password fallback"
            }
          ]
        },
        {
          "name": "BiometricResult",
          "description": "Authentication result",
          "properties": [
            {
              "name": "verified",
              "type": "boolean",
              "required": true,
              "description": "Whether authentication succeeded"
            },
            {
              "name": "biometricType",
              "type": "'fingerprint' | 'face' | 'iris' | 'none'",
              "required": false,
              "description": "Type used"
            }
          ]
        },
        {
          "name": "BiometricAvailability",
          "description": "Biometric capability info",
          "properties": [
            {
              "name": "isAvailable",
              "type": "boolean",
              "required": true,
              "description": "Whether available"
            },
            {
              "name": "biometricTypes",
              "type": "BiometricType[]",
              "required": true,
              "description": "Available types"
            },
            {
              "name": "reason",
              "type": "string",
              "required": false,
              "description": "Why unavailable"
            }
          ]
        }
      ]
    },
    "haptics": {
      "description": "Haptic feedback types",
      "types": [
        {
          "name": "HapticImpactStyle",
          "description": "Impact feedback intensity",
          "properties": [
            {
              "name": "type",
              "type": "'light' | 'medium' | 'heavy'",
              "required": true,
              "description": "Impact style"
            }
          ]
        },
        {
          "name": "HapticNotificationType",
          "description": "Notification feedback type",
          "properties": [
            {
              "name": "type",
              "type": "'success' | 'warning' | 'error'",
              "required": true,
              "description": "Notification type"
            }
          ]
        }
      ]
    },
    "device": {
      "description": "Device information types",
      "types": [
        {
          "name": "DeviceInfo",
          "description": "Device hardware and OS info",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": false,
              "description": "Device name"
            },
            {
              "name": "model",
              "type": "string",
              "required": true,
              "description": "Device model"
            },
            {
              "name": "platform",
              "type": "Platform",
              "required": true,
              "description": "OS platform"
            },
            {
              "name": "osVersion",
              "type": "string",
              "required": true,
              "description": "OS version"
            },
            {
              "name": "manufacturer",
              "type": "string",
              "required": false,
              "description": "Manufacturer (Android)"
            },
            {
              "name": "isVirtual",
              "type": "boolean",
              "required": true,
              "description": "Is simulator/emulator"
            }
          ]
        },
        {
          "name": "BatteryInfo",
          "description": "Battery status",
          "properties": [
            {
              "name": "batteryLevel",
              "type": "number",
              "required": true,
              "description": "Level (0-1)"
            },
            {
              "name": "isCharging",
              "type": "boolean",
              "required": true,
              "description": "Is charging"
            }
          ]
        }
      ]
    },
    "network": {
      "description": "Network connectivity types",
      "types": [
        {
          "name": "NetworkStatus",
          "description": "Network connectivity status",
          "properties": [
            {
              "name": "connected",
              "type": "boolean",
              "required": true,
              "description": "Has connectivity"
            },
            {
              "name": "connectionType",
              "type": "'wifi' | 'cellular' | 'none' | 'unknown'",
              "required": true,
              "description": "Connection type"
            }
          ]
        }
      ]
    },
    "push": {
      "description": "Push notification types",
      "types": [
        {
          "name": "PushToken",
          "description": "Push registration token",
          "properties": [
            {
              "name": "value",
              "type": "string",
              "required": true,
              "description": "Token value"
            }
          ]
        },
        {
          "name": "PushNotification",
          "description": "Received notification",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Notification ID"
            },
            {
              "name": "title",
              "type": "string",
              "required": false,
              "description": "Title"
            },
            {
              "name": "body",
              "type": "string",
              "required": false,
              "description": "Body text"
            },
            {
              "name": "data",
              "type": "Record<string, unknown>",
              "required": true,
              "description": "Custom data"
            }
          ]
        },
        {
          "name": "NotificationChannel",
          "description": "Android notification channel",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Channel ID"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Display name"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Description"
            },
            {
              "name": "importance",
              "type": "number",
              "required": false,
              "description": "Importance (1-5)"
            }
          ]
        }
      ]
    },
    "lifecycle": {
      "description": "App lifecycle types",
      "types": [
        {
          "name": "AppStateChange",
          "description": "App state change event",
          "properties": [
            {
              "name": "isActive",
              "type": "boolean",
              "required": true,
              "description": "Is app active"
            }
          ]
        },
        {
          "name": "AppUrlOpen",
          "description": "Deep link open event",
          "properties": [
            {
              "name": "url",
              "type": "string",
              "required": true,
              "description": "Opened URL"
            }
          ]
        }
      ]
    },
    "errors": {
      "description": "Error types and type guards",
      "types": [
        {
          "name": "NativeError",
          "description": "Base error class for native errors",
          "properties": [
            {
              "name": "code",
              "type": "string",
              "required": true,
              "description": "Error code"
            },
            {
              "name": "message",
              "type": "string",
              "required": true,
              "description": "Error message"
            }
          ]
        },
        {
          "name": "PermissionDeniedError",
          "description": "User denied permission",
          "properties": [
            {
              "name": "feature",
              "type": "NativeFeature",
              "required": true,
              "description": "Feature denied"
            }
          ]
        },
        {
          "name": "FeatureUnavailableError",
          "description": "Feature not available on platform",
          "properties": [
            {
              "name": "feature",
              "type": "NativeFeature",
              "required": true,
              "description": "Unavailable feature"
            }
          ]
        },
        {
          "name": "UserCancelledError",
          "description": "User cancelled operation",
          "properties": []
        }
      ]
    }
  },
  "quickReference": {
    "installation": "npm install @classic-homes/native @capacitor/core",
    "basicUsage": "import { initNative, getPlatformInfo } from '@classic-homes/native';\nimport { useGeolocation, useCamera } from '@classic-homes/native/react';\n// or\nimport { useGeolocation, useCamera } from '@classic-homes/native/svelte';\n\n// Initialize at app startup\nconst platform = initNative({ debug: true });",
    "peerDependency": "@capacitor/core@^6.0.0",
    "optionalPlugins": [
      "@capacitor/camera",
      "@capacitor/geolocation",
      "@capacitor/preferences",
      "@capgo/capacitor-native-biometric",
      "@capacitor/haptics",
      "@capacitor/device",
      "@capacitor/network",
      "@capacitor/push-notifications",
      "@capacitor/app"
    ]
  }
}